import type { AttributeDefinitionApi, CreateAttributeRequestApi } from '../types/attributes-api.types';
import {
  OPTION_ATTRIBUTE_TYPES,
  RANGE_ATTRIBUTE_TYPES,
  REST_ATTRIBUTE_TYPES,
  type CustomFieldDataType,
  type CustomFieldDefinition,
  type RestAttributeType,
} from '../types/custom-field-definitions.types';

export const REST_TYPE_LABELS: Record<RestAttributeType, string> = {
  string: 'String',
  number: 'Number',
  boolean: 'Boolean',
  date: 'Date',
  enum: 'Enum',
  multienum: 'Multi-select',
};

export const REST_TYPE_TO_DATA_TYPE: Record<RestAttributeType, CustomFieldDataType> = {
  string: 'Text',
  number: 'Number',
  boolean: 'Boolean',
  date: 'Date',
  // Single choice: a backend `enum` holds one value from its options (#50) and rejects a list, so
  // rendering it as a checkbox list (MultiSelect) sent `["pro"]` and every save returned 400.
  // The multi-value type is `multienum` (#231), not a reading of `enum`.
  enum: 'Dropdown',
  multienum: 'MultiSelect',
};

const DATA_TYPE_TO_REST_TYPE: Partial<Record<CustomFieldDataType, RestAttributeType>> = {
  Text: 'string',
  Number: 'number',
  NumberRange: 'number',
  Boolean: 'boolean',
  Date: 'date',
  Dropdown: 'enum',
  MultiSelect: 'multienum',
};

const isRestAttributeType = (value: string): value is RestAttributeType =>
  REST_ATTRIBUTE_TYPES.includes(value as RestAttributeType);

const readTrimmedString = (...values: Array<string | null | undefined>): string => {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return '';
};

const readStringArray = (...values: Array<string[] | null | undefined>): string[] => {
  for (const value of values) {
    if (Array.isArray(value)) {
      return value.map((item) => item.trim()).filter(Boolean);
    }
  }
  return [];
};

export const getAttributeTypeLabel = (definition: CustomFieldDefinition): string => {
  const restType = getRestAttributeType(definition);
  return REST_TYPE_LABELS[restType];
};

export const getRestAttributeType = (definition: CustomFieldDefinition): RestAttributeType => {
  if (definition.RestType && isRestAttributeType(definition.RestType)) {
    return definition.RestType;
  }

  const dataType = definition.DataType?.trim();
  if (dataType && dataType in DATA_TYPE_TO_REST_TYPE) {
    return DATA_TYPE_TO_REST_TYPE[dataType as CustomFieldDataType] ?? 'string';
  }

  return 'string';
};

export const mapAttributeApiToDefinition = (
  item: AttributeDefinitionApi | null | undefined
): CustomFieldDefinition | null => {
  if (!item || typeof item !== 'object') return null;

  const key = readTrimmedString(item.key, item.Key);
  const label = readTrimmedString(item.label, item.Label);
  if (!key || !label) return null;

  const rawType = readTrimmedString(item.type, item.Type).toLowerCase();
  const restType: RestAttributeType = isRestAttributeType(rawType) ? rawType : 'string';
  const enumOptions = readStringArray(item.enumOptions, item.EnumOptions);
  const itemId = readTrimmedString(item.itemId, item.ItemId, item.id) || key;
  const usageCount = Number(item.usageCount ?? item.UsageCount ?? 0);

  return {
    ItemId: itemId,
    Key: key,
    Label: label,
    Category: 'Custom Fields',
    DataType: REST_TYPE_TO_DATA_TYPE[restType],
    Options: OPTION_ATTRIBUTE_TYPES.includes(restType) ? enumOptions : [],
    IsFilterable: true,
    IsDefault: false,
    isDeleted: false,
    Required: Boolean(item.required ?? item.Required),
    UsageCount: Number.isFinite(usageCount) ? usageCount : 0,
    RestType: restType,
    IsRangeFilterable: Boolean(
      item.isRangeFilterable ?? item.IsRangeFilterable ?? item.isRange ?? item.IsRange
    ),
  };
};

export const toCreateAttributeRequest = (input: {
  key: string;
  label: string;
  type: RestAttributeType;
  enumOptions?: string[];
  required: boolean;
  isRangeFilterable?: boolean;
}): CreateAttributeRequestApi => {
  const payload: CreateAttributeRequestApi = {
    key: input.key.trim(),
    label: input.label.trim(),
    type: input.type,
    required: input.required,
  };

  if (OPTION_ATTRIBUTE_TYPES.includes(input.type)) {
    payload.enumOptions = (input.enumOptions ?? []).map((option) => option.trim()).filter(Boolean);
  }

  if (RANGE_ATTRIBUTE_TYPES.includes(input.type)) {
    payload.IsRangeFilterable = input.isRangeFilterable === true;
  }

  return payload;
};
